/**
 * What a raw query might be, and therefore which lookup can resolve it.
 *
 * Every kind but `freeText` names an indexed lookup, so each is a single
 * keyed read rather than a scan.
 */
export type SearchIntent =
  | { kind: "stripeAccount"; value: string }
  | { kind: "stripeCustomer"; value: string }
  | { kind: "stripeSubscription"; value: string }
  | { kind: "stripeCharge"; value: string }
  | { kind: "email"; value: string }
  | { kind: "businessId"; value: string }
  | { kind: "transactionId"; value: string }
  | { kind: "invoiceId"; value: string }
  | { kind: "freeText"; value: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Stripe id prefixes this app can actually resolve, mapped to the lookup that
 * resolves them.
 *
 * `acct_`, `cus_` and `sub_` hit BusinessProfile secondary indexes; `ch_` and
 * `py_` hit Transaction's `sourceChargeId` index. Payment intents (`pi_`) are
 * deliberately absent — nothing indexes them, so offering the branch would
 * promise a lookup that cannot run.
 */
const STRIPE_PREFIXES: [string, SearchIntent["kind"]][] = [
  ["acct_", "stripeAccount"],
  ["cus_", "stripeCustomer"],
  ["sub_", "stripeSubscription"],
  ["ch_", "stripeCharge"],
  ["py_", "stripeCharge"],
];

/**
 * Ordered candidate lookups for a raw query, best guess first.
 *
 * Returns a list rather than one intent because a UUID is genuinely
 * ambiguous: BusinessProfile, Transaction and Invoice all key on `a.id()`, so
 * the only way to tell them apart is to probe. Callers fan the list out in
 * parallel and render whichever probes come back.
 *
 * Stripe ids resolve to exactly one lookup and suppress the free-text branch,
 * since an opaque token will never match an index entry. Emails keep it, so a
 * merchant whose name contains the address can still rank alongside.
 */
export function classifyQuery(raw: string): SearchIntent[] {
  const trimmed = raw.trim();
  if (trimmed === "") return [];

  for (const [prefix, kind] of STRIPE_PREFIXES) {
    if (trimmed.startsWith(prefix)) {
      return [{ kind, value: trimmed } as SearchIntent];
    }
  }

  if (UUID.test(trimmed)) {
    const id = trimmed.toLowerCase();
    return [
      { kind: "businessId", value: id },
      { kind: "transactionId", value: id },
      { kind: "invoiceId", value: id },
    ];
  }

  if (trimmed.includes("@")) {
    return [
      { kind: "email", value: trimmed.toLowerCase() },
      { kind: "freeText", value: trimmed },
    ];
  }

  return [{ kind: "freeText", value: trimmed }];
}
